import { Box, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useLingui } from "@lingui/react";
import { t } from "@lingui/macro";
import {
  ImperialMetricDisplayPreference,
  WahooWorkoutTypeFamily,
} from "@WahooFitness/cloud-client-types";
import {
  WfButton,
  WfIconContentInfoRegular,
  WfLegend,
  WfMessage,
  WfSnail,
  WfSnailVariant,
} from "@WahooFitness/redesignr";
import { ComponentProps } from "react";
import EstimateChip from "../EstimateChip";
import { useUserContext } from "@WahooFitness/wahoo-offline-mfe";

type SnailSectionProps = {
  workoutFamily: WahooWorkoutTypeFamily.Running | WahooWorkoutTypeFamily.Cycling;
  variant?: WfSnailVariant;
  hasProfile: boolean;
  profileTypeName?: string;
  profileTypeIcon?: React.ReactNode;
  profileDescription?: string;
  dimensions: ComponentProps<typeof WfSnail>["dimensions"];
  legendItems: ComponentProps<typeof WfLegend>["loadItems"];
  openInfoDrawer: () => void;
  isProfileEstimate?: boolean;
  isProfileManual?: boolean;
  openEstimateExplanationDrawer?: () => void;
  hasRiderTypeMismatch?: boolean | undefined;
  handleDismissRiderTypeAlert?: () => void;
  userDismissedRiderTypeAlert?: boolean | undefined;
};

const SnailSection = ({
  workoutFamily,
  variant = WfSnailVariant.Standard,
  hasProfile,
  profileTypeName,
  profileTypeIcon,
  profileDescription,
  dimensions,
  legendItems,
  openInfoDrawer,
  isProfileEstimate,
  isProfileManual,
  openEstimateExplanationDrawer,
  hasRiderTypeMismatch,
  handleDismissRiderTypeAlert,
  userDismissedRiderTypeAlert,
}: SnailSectionProps) => {
  const { i18n } = useLingui();
  const { currentUser } = useUserContext();
  const metric =
    (currentUser?.display_preference?.speed_distance ?? ImperialMetricDisplayPreference.metric) ===
    ImperialMetricDisplayPreference.metric;

  const { breakpoints } = useTheme();
  const isSmallScreen = useMediaQuery(breakpoints.down("sm"));

  return (
    <Box display="flex" flexDirection="column" alignItems="center" width="100%">
      {profileTypeName && (
        <Stack
          direction="row"
          alignItems="center"
          px={2}
          width="100%"
          justifyContent="space-between"
        >
          <Box display="flex" alignItems="center" flexDirection="row" gap={1.5}>
            <Typography variant="ui-4xl-bold">{profileTypeName}</Typography>
            {profileTypeIcon && (
              <Box width={20} height={20}>
                {profileTypeIcon}
              </Box>
            )}
          </Box>
          {(isProfileEstimate || isProfileManual) && (
            <EstimateChip isManual={isProfileManual} handleClick={openEstimateExplanationDrawer} />
          )}
        </Stack>
      )}
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        width="100%"
        position="relative"
        pt={1}
      >
        <Box px={isSmallScreen ? 0 : 2} width="100%">
          {/* @ts-expect-error WfSnail uses a discriminated union on workoutFamily but this component handles both cycling and running */}
          <WfSnail
            workoutFamily={workoutFamily}
            variant={variant}
            empty={!hasProfile}
            dimensions={dimensions}
            i18nContext={i18n}
            metric={metric}
          />
        </Box>
        {hasProfile && (
          <Box
            display="flex"
            flexDirection="row"
            gap={1}
            id="info-container"
            position="absolute"
            bottom={16}
            left={16}
          >
            <WfButton
              hierarchy="text"
              intent="default"
              size="medium"
              onClick={openInfoDrawer}
              iconOnly
              aria-label={t`How we calculate`}
            >
              <WfIconContentInfoRegular fontSize="small" />
            </WfButton>
          </Box>
        )}
      </Box>
      <Box display="flex" px={2} flexDirection="column" gap={2}>
        <Box width="100%" py={2}>
          <WfLegend loadItems={legendItems} wrapItems />
        </Box>
        {!userDismissedRiderTypeAlert && hasRiderTypeMismatch && (
          <WfMessage
            title={t`Noticing a different Rider Type than the one in SYSTM?`}
            hideTitleIcon
            description={t`No, you didn't suddenly transform overnight! We've just upgraded to a smarter, more accurate algorithm that better captures your true abilities.`}
            hierarchy="info"
            onClose={handleDismissRiderTypeAlert}
          />
        )}
      </Box>

      {profileDescription && (
        <Box px={2} pt={2} pb={1.5}>
          <Typography variant="prose-base" color="text.primary">
            {profileDescription}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SnailSection;
